import React, { useState } from 'react';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Composants UI
import Button from '../ui/Button/Button';
import Input from '../ui/Input/Input';
import TextArea from '../ui/TextArea/TextArea';
import Modal from '../ui/Modal/Modal';
import './EditNewsForm.css';

const EditNewsForm = ({ news, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    titre: news.titre,
    description: news.description,
    categorie: news.categorie,
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(news.imageUrl);
  const [shouldRemoveImage, setShouldRemoveImage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Catégories prédéfinies
  const categories = [
    'E-liquide',
    'E-cigarette',
    'CBD',
    'Accessoire'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setShouldRemoveImage(false);
    }
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(null);
    setShouldRemoveImage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const db = getFirestore();
      const storage = getStorage();
      const newsRef = doc(db, 'nouveautes', news.id);
      
      let updateData = {
        titre: formData.titre,
        description: formData.description,
        categorie: formData.categorie,
      };

      if (imageFile) {
        if (news.imageUrl) {
          try {
            const oldImageRef = ref(storage, news.imageUrl);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.log("L'ancienne image n'existe peut-être plus:", error);
          }
        }

        const timestamp = Date.now();
        const storageRef = ref(storage, `nouveautes/${timestamp}_${imageFile.name}`);
        await uploadBytes(storageRef, imageFile);
        const newImageUrl = await getDownloadURL(storageRef);
        updateData.imageUrl = newImageUrl;
      } else if (shouldRemoveImage && news.imageUrl) {
        // Supprimer l'image existante
        try {
          const oldImageRef = ref(storage, news.imageUrl);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.log("L'ancienne image n'existe peut-être plus:", error);
        }
        updateData.imageUrl = null;
      }

      await updateDoc(newsRef, updateData);
      onSuccess();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Une erreur est survenue lors de la mise à jour');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title="Modifier l'actualité"
      actions={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </>
      }
      className="edit-news-modal"
    >
      <div className="modal-sections">
        <form onSubmit={handleSubmit} className="form-section">
          <div className="form-group">
            <Input
              type="text"
              label="Titre"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <TextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Catégorie</label>
            <select
              name="categorie"
              value={formData.categorie}
              onChange={handleChange}
              required
              className="category-select"
            >
              <option value="">Sélectionner une catégorie</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </form>

        <div className="image-section">
          <div className="form-group">
            <label>Image</label>
            <div className="image-upload-zone">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="image-input"
                id="image-input"
              />
              {!imagePreview ? (
                <label htmlFor="image-input" className="upload-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>Glisser une image ou cliquer pour sélectionner</span>
                </label>
              ) : (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Prévisualisation" />
                  <button 
                    type="button" 
                    className="remove-image"
                    onClick={handleImageRemove}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditNewsForm; 